import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Tag, Tooltip, Dropdown, Modal, TimePicker } from 'antd';
import { CopyOutlined, MoreOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Link from '@/components/ui/Link';
import { useCanCheck } from '@/ability/helpers';
import { useAuthStore } from '@stores/auth.store';
import { colors } from '@utils/colorSystem';
import {
    getAttendance,
    createAttendance,
    updateAttendance,
    deleteAttendance,
    getHolidays,
    getUpcomingEvents,
    createHoliday,
    updateHoliday,
    deleteHoliday,
} from '../services/attendance.service';
import { getEmployees } from '../../employee/services/employee.service';
import { getAttendanceColumns } from '../columns/attendanceColumns';
import { ENV } from '@configs/env';

const StyledEmailLink = styled(Link)`
    color: var(--ant-color-primary, #7543dc) !important;
    font-weight: 400 !important;
    text-decoration: none !important;
    &:hover {
        text-decoration: none !important;
        color: var(--ant-color-primary-hover, #5b21b6) !important;
    }
`;

export const ATTENDANCE_QUERY_KEY = ['hr', 'attendance'];

const STATUS_TAB = { ALL: 'all', PRESENT: 'present', ABSENT: 'absent', HALF_DAY: 'half_day', LEAVE: 'leave', HOLIDAY: 'holiday' };
const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

export const ATTENDANCE_STATUS_OPTIONS = [
    { value: 'present', label: 'Present' },
    { value: 'absent', label: 'Absent' },
    { value: 'half_day', label: 'Half day' },
    { value: 'leave', label: 'Leave' },
    { value: 'holiday', label: 'Holiday' },
];

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toISOString().slice(0, 10);
    } catch {
        return dateStr;
    }
};

export const useAttendance = () => {
    const queryClient = useQueryClient();
    const [createForm] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [filterDate, setFilterDate] = useState(null);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [filterCheckIn, setFilterCheckIn] = useState(undefined);
    const [filterCheckOut, setFilterCheckOut] = useState(undefined);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [calendarYear, setCalendarYear] = useState(dayjs().year());

    // Server-side pagination states
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Reset page to 1 whenever any filter or search changes
    useEffect(() => {
        setPage(1);
    }, [searchText, statusTab, filterDate, filterStatus, filterCheckIn, filterCheckOut]);

    const createFormStatus = Form.useWatch('status', createForm);
    const showCheckInOutInCreateModal = useMemo(() => {
        const s = String(createFormStatus ?? 'present').toLowerCase();
        return s !== 'absent' && s !== 'leave';
    }, [createFormStatus]);

    useEffect(() => {
        const s = String(createFormStatus ?? '').toLowerCase();
        if (s === 'absent' || s === 'leave') {
            createForm.setFieldsValue({ checkIn: undefined, checkOut: undefined });
        }
    }, [createFormStatus, createForm]);

    const can = useCanCheck();
    const currentUserId = useAuthStore((s) => s.user?.id);
    const canView = can('hr.attendance.view');
    const canCreate = can('hr.attendance.create');
    const canUpdate = can('hr.attendance.update');
    const canDelete = can('hr.attendance.delete');
    const tenantId = useAuthStore((s) => s.user?.tenantId || s.user?.tenant_id);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editForm] = Form.useForm();

    useEffect(() => {
        if (!tenantId) return;

        const baseUrl = ENV.API_URL || '';
        const sseUrl = `${baseUrl.replace(/\/$/, '')}/hr/attendance/stream?tenantId=${tenantId}`;
        console.log('[SSE] Connecting to:', sseUrl);
        const eventSource = new EventSource(sseUrl);

        eventSource.onmessage = (event) => {
            console.log('[SSE] Attendance update received:', event.data);
            try {
                queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
                message.info('Real-time attendance update detected!');
            } catch (err) {
                console.error('[SSE] Error handling message:', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('[SSE] Connection Error:', err);
        };

        return () => {
            eventSource.close();
            console.log('[SSE] Connection closed');
        };
    }, [tenantId, queryClient]);

    const statusFilterVal = filterStatus || (statusTab !== STATUS_TAB.ALL ? statusTab : undefined);
    const dateStr = filterDate ? filterDate.format('YYYY-MM-DD') : undefined;

    const { data: response, isError: attendanceError } = useQuery({
        queryKey: [
            ...ATTENDANCE_QUERY_KEY,
            {
                page,
                limit,
                sortOrder: 'desc',
                search: searchText,
                status: statusFilterVal,
                dateFrom: dateStr,
                dateTo: dateStr,
                checkIn: filterCheckIn,
                checkOut: filterCheckOut,
            },
        ],
        queryFn: () =>
            getAttendance({
                page,
                limit,
                sortOrder: 'desc',
                search: searchText || undefined,
                status: statusFilterVal,
                dateFrom: dateStr,
                dateTo: dateStr,
                checkIn: filterCheckIn,
                checkOut: filterCheckOut,
            }),
        enabled: !!canView,
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
    });

    const { data: employeesResponse } = useQuery({
        queryKey: ['hr', 'employees', 'list'],
        queryFn: () => getEmployees({ page: 1, limit: 100 }),
        enabled: createModalOpen && canCreate,
    });

    const attendanceList = response?.data ?? [];
    const total = response?.total ?? 0;

    // Use counts calculated and returned by backend for the current filters
    const presentCount = response?.presentCount ?? 0;
    const absentCount = response?.absentCount ?? 0;

    const activeFilterCount = [filterDate, filterStatus, filterCheckIn, filterCheckOut].filter(Boolean).length;

    const clearFilters = useCallback(() => {
        setFilterDate(null);
        setFilterStatus(undefined);
        setFilterCheckIn(undefined);
        setFilterCheckOut(undefined);
    }, []);

    const checkInOptions = useMemo(() => {
        const unique = [...new Set(attendanceList.map((a) => a.checkIn ?? a.check_in).filter(Boolean))].sort();
        return unique.map((v) => ({ value: v, label: v }));
    }, [attendanceList]);

    const checkOutOptions = useMemo(() => {
        const unique = [...new Set(attendanceList.map((a) => a.checkOut ?? a.check_out).filter(Boolean))].sort();
        return unique.map((v) => ({ value: v, label: v }));
    }, [attendanceList]);

    // Data is already filtered by server, so filteredList is just attendanceList
    const filteredList = attendanceList;

    const tableData = useMemo(
        () =>
            filteredList.map((a) => ({
                key: a.id,
                id: a.id,
                userName: a.userName ?? a.user?.name ?? '—',
                userEmail: a.userEmail ?? a.user?.email ?? '—',
                userId: a.userId,
                date: a.date,
                status: a.status,
                checkIn: a.checkIn ?? a.check_in,
                checkOut: a.checkOut ?? a.check_out,
                totalHoursWorked: a.totalHoursWorked ?? a.total_hours_worked ?? null,
                totalHoursJob: a.totalHoursJob ?? a.total_hours_job ?? null,
                lateTime: a.lateTime ?? a.late_time ?? null,
                overtime: a.overtime ?? a.overtime ?? null,
                graceTime: a.graceTime ?? a.grace_time ?? null,
                notes: a.notes,
            })),
        [filteredList],
    );

    const createAttendanceMutation = useMutation({
        mutationFn: (body) => createAttendance(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
        },
    });

    const updateAttendanceMutation = useMutation({
        mutationFn: ({ id, body }) => updateAttendance(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
        },
    });

    const deleteAttendanceMutation = useMutation({
        mutationFn: (id) => deleteAttendance(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
        },
    });

    const employeeOptions = useMemo(() => {
        const data = employeesResponse?.data ?? [];
        const list = Array.isArray(data) ? data : [];
        return list
            .filter((e) => e.id !== currentUserId)
            .filter((e) => {
                const roles = Array.isArray(e.roles) ? e.roles : [];
                return !roles.some((r) => {
                    const name = (r && (r.name ?? r.roleName ?? r.label ?? r)) || '';
                    return String(name).toLowerCase() === 'company admin';
                });
            })
            .map((e) => ({
                value: e.id,
                label: e.name ? `${e.name} (${e.email})` : e.email,
            }));
    }, [employeesResponse, currentUserId]);

    const closeCreateModal = () => {
        setCreateModalOpen(false);
        createForm.resetFields();
    };

    const onSubmitCreate = () => {
        createForm.validateFields().then((values) => {
            const statusNorm = String(values.status || 'present').toLowerCase();
            const skipCheckTimes = statusNorm === 'absent' || statusNorm === 'leave';
            const payload = {
                userId: values.userId,
                date: values.date
                    ? typeof values.date === 'string'
                        ? values.date
                        : values.date.format?.('YYYY-MM-DD')
                    : undefined,
                status: values.status || 'present',
                checkIn: skipCheckTimes
                    ? undefined
                    : values.checkIn
                      ? values.checkIn.format
                          ? values.checkIn.format('HH:mm')
                          : values.checkIn
                      : undefined,
                checkOut: skipCheckTimes
                    ? undefined
                    : values.checkOut
                      ? values.checkOut.format
                          ? values.checkOut.format('HH:mm')
                          : values.checkOut
                      : undefined,
            };
            if (!payload.userId || !payload.date) {
                message.error('User and date are required.');
                return;
            }
            createAttendanceMutation.mutate(payload, {
                onSuccess: () => {
                    message.success('Attendance recorded.');
                    closeCreateModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to record attendance.');
                },
            });
        });
    };

    const onSubmitClear = (record) => {
        const payload = {
            userId: record.userId,
            date: record.date ? (typeof record.date === 'string' ? record.date.slice(0, 10) : dayjs(record.date).format('YYYY-MM-DD')) : undefined,
            status: 'absent',
            checkIn: undefined,
            checkOut: undefined,
            notes: 'Cleared and marked absent (dev only)',
        };
        createAttendanceMutation.mutate(payload, {
            onSuccess: () => {
                message.success(`Attendance cleared and marked absent for ${record.userName}.`);
            },
            onError: (err) => {
                message.error(err?.response?.data?.message || 'Failed to clear attendance.');
            },
        });
    };

    const openEditModal = useCallback((record) => {
        setEditingRecord(record);
        const statusNorm = (record.status || 'present').toLowerCase();
        editForm.setFieldsValue({
            status: statusNorm,
            checkIn: record.checkIn ? dayjs(`2000-01-01T${record.checkIn}`) : undefined,
            checkOut: record.checkOut ? dayjs(`2000-01-01T${record.checkOut}`) : undefined,
            notes: record.notes || '',
        });
        setEditModalOpen(true);
    }, [editForm]);

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEditingRecord(null);
        editForm.resetFields();
    }, [editForm]);

    const onSubmitEdit = useCallback(() => {
        editForm.validateFields().then((values) => {
            const statusNorm = String(values.status || 'present').toLowerCase();
            const skipTimes = statusNorm === 'absent' || statusNorm === 'leave';
            const payload = {
                status: values.status,
                checkIn: skipTimes ? undefined : (values.checkIn ? (values.checkIn.format ? values.checkIn.format('HH:mm') : values.checkIn) : undefined),
                checkOut: skipTimes ? undefined : (values.checkOut ? (values.checkOut.format ? values.checkOut.format('HH:mm') : values.checkOut) : undefined),
                notes: values.notes || undefined,
            };
            updateAttendanceMutation.mutate({ id: editingRecord.id, body: payload }, {
                onSuccess: () => {
                    message.success('Attendance updated.');
                    closeEditModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to update attendance.');
                },
            });
        });
    }, [editForm, editingRecord, updateAttendanceMutation, closeEditModal]);

    const onConfirmDelete = useCallback((record) => {
        Modal.confirm({
            title: 'Clear Attendance',
            content: `Delete the attendance record for ${record.userName} on ${record.date ? String(record.date).slice(0, 10) : ''}? This cannot be undone.`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => {
                deleteAttendanceMutation.mutate(record.id, {
                    onSuccess: () => message.success(`Attendance cleared for ${record.userName}.`),
                    onError: (err) => message.error(err?.response?.data?.message || 'Failed to delete attendance.'),
                });
            },
        });
    }, [deleteAttendanceMutation]);

    const handleCopyEmail = useCallback((email, e) => {
        e?.stopPropagation?.();
        if (!email) return;
        navigator.clipboard?.writeText(email).then(
            () => message.success('Email copied'),
            () => message.error('Failed to copy'),
        );
    }, []);

    const columns = useMemo(
        () =>
            getAttendanceColumns({
                handleCopyEmail,
                openEditModal,
                onConfirmDelete,
                canUpdate,
                canDelete,
            }),
        [handleCopyEmail, openEditModal, onConfirmDelete, canUpdate, canDelete],
    );

    // Holidays query
    const { data: holidaysResponse, refetch: refetchHolidays } = useQuery({
        queryKey: ['hr', 'holidays', { year: calendarYear, limit: 100 }],
        queryFn: () => getHolidays({ year: String(calendarYear), limit: 100 }),
        enabled: !!canView,
    });

    // Upcoming events query
    const { data: upcomingEvents, refetch: refetchUpcomingEvents } = useQuery({
        queryKey: ['hr', 'upcoming-events'],
        queryFn: () => getUpcomingEvents(),
        enabled: !!canView,
    });

    // Create Holiday mutation
    const createHolidayMutation = useMutation({
        mutationFn: (body) => createHoliday(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] });
        },
    });

    // Update Holiday mutation
    const updateHolidayMutation = useMutation({
        mutationFn: ({ id, body }) => updateHoliday(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] });
        },
    });

    // Delete Holiday mutation
    const deleteHolidayMutation = useMutation({
        mutationFn: (id) => deleteHoliday(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ATTENDANCE_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['hr', 'holidays'] });
        },
    });

    return {
        attendanceList,
        total,
        presentCount,
        absentCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        canView,
        canCreate,
        canUpdate,
        canDelete,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        onSubmitClear,
        closeCreateModal,
        createAttendancePending: createAttendanceMutation.isPending,
        // Edit modal
        editModalOpen,
        editingRecord,
        editForm,
        onSubmitEdit,
        closeEditModal,
        updateAttendancePending: updateAttendanceMutation.isPending,
        deleteAttendancePending: deleteAttendanceMutation.isPending,
        ATTENDANCE_STATUS_OPTIONS,
        filterDate,
        setFilterDate,
        filterStatus,
        setFilterStatus,
        filterCheckIn,
        setFilterCheckIn,
        filterCheckOut,
        setFilterCheckOut,
        filterModalOpen,
        setFilterModalOpen,
        activeFilterCount,
        clearFilters,
        checkInOptions,
        checkOutOptions,
        showCheckInOutInCreateModal,
        page,
        setPage,
        limit,
        setLimit,
        holidays: holidaysResponse?.data ?? [],
        upcomingEvents: upcomingEvents ?? [],
        createHolidayPending: createHolidayMutation.isPending,
        updateHolidayPending: updateHolidayMutation.isPending,
        deleteHolidayPending: deleteHolidayMutation.isPending,
        createHoliday: createHolidayMutation.mutateAsync,
        updateHoliday: updateHolidayMutation.mutateAsync,
        deleteHoliday: deleteHolidayMutation.mutateAsync,
        refetchHolidays,
        refetchUpcomingEvents,
        calendarYear,
        setCalendarYear,
    };
};
